import { useEffect, useState } from 'react';
import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { DataBox } from '../../../../Components/DataBox/DataBox';
import UserFormDialog from './UserFormDialog';
import { ConfirmActionDialog } from '../../../../Components/Common/ConfirmActionDialog/ConfirmActionDialog';
import { getUsers } from '../../../../Services/Users';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { assignUserToJob, getJobUsers, unAssignUserFromJob } from '../../../../Services/Jobs';
import { ROLES } from '../../../../Types/constants';
import { Loader } from '../../../../Components/Loader/Loader'; 

export const JobTeam = ({ jobId, canEdit }: { jobId: string, canEdit: boolean }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [showConfirmUnassignDialog, setShowConfirmUnassignDialog] = useState(false);
  const [openUserForm, setOpenUserForm] = useState(false);
  const [fromTA, setFromTa] = useState(false);
  const [teamTA, setTeamTA] = useState<User[]>([]);
  const [teamOthers, setTeamOthers] = useState<User[]>([]);
  const [userToUnassign, setUserToUnassign] = useState<User | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const errorMessage = t('toast.errors.something_went_wrong');
  const assignSuccessMessage = t('jobs.team.assign_user_success_message');
  const unassignSuccessMessage = t('jobs.team.unassign_user_success_message');

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['getUsers'],
    queryFn: async () => {
      const response = await getUsers({
        page: 1,
        count: 1000,
      });
      return response.items;
    },
  });

  const { data: jobUsers, isLoading: isLoadingJobUsers } = useQuery({
    queryKey: ['getJobUsers'],
    queryFn: async () => {
      const response = await getJobUsers(jobId, {
        page: 1,
        count: 1000,
      });
      return response.items;
    },
  });

  const { mutate: assignUserMutation } = useMutation({
    mutationFn: (userId: string) => assignUserToJob(jobId, userId),
    onError: () => {
      toast.error(errorMessage);
    },
    onSuccess: () => {
      toast.success(assignSuccessMessage);
      setOpenUserForm(false);
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getJobUsers'] });
    },
  });

  const { mutate: unAssignUserMutation } = useMutation({
    mutationFn: async (userId: string) => {
      setIsUnassigning(true); 
      await unAssignUserFromJob(jobId, userId);
    },
    onError: () => {
      toast.error(errorMessage);
      setIsUnassigning(false); 
    },
    onSuccess: () => {
      toast.success(unassignSuccessMessage);
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getJobUsers'] });
      setIsUnassigning(false);
      setShowConfirmUnassignDialog(false);
    },
  });

  const handleAddUserFromTa = () => {
    setFromTa(true);
    setOpenUserForm(true);
  };

  const handleAddOtherUser = () => {
    setFromTa(false);
    setOpenUserForm(true);
  };

  const handleUserAdd = (user: User) => {
    assignUserMutation(user.id!);
    setOpenUserForm(false);
  };

  const handleUnassignClicked = (user: User) => {
    setUserToUnassign(user);
    setShowConfirmUnassignDialog(true);
  };

  const handleUnassignConfirm = () => {
    if (userToUnassign) {
      if (userToUnassign?.id) {
        unAssignUserMutation(userToUnassign.id); 
      }
    }
  };

  useEffect(() => {
    if (jobUsers) {
      setTeamTA(jobUsers.filter((user) => user.roles?.includes(ROLES.admin) || user.roles?.includes(ROLES.recruiter)));
      setTeamOthers(jobUsers.filter((user) => user.roles?.includes(ROLES.internal) || user.roles?.includes(ROLES.operations)));
    }
  }, [jobUsers]);

  return (
    <div style={{ display: 'flex' }}>
      {<Loader isLoading={isLoadingUsers || isLoadingJobUsers} />}
      <DataBox
        title={t('jobs.team.hrTeam')}
        handleAdd={canEdit ? () => handleAddUserFromTa() : undefined}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {teamTA?.map((user) => (
                <TableRow key={user.id} style={{ height: '55px' }}>
                  <TableCell component="th" scope="row" style={{ fontWeight: 'bold' }}>
                    {user.fullName}
                  </TableCell>
                  {canEdit && (
                    <TableCell component="th" scope="row" style={{ textAlign: 'right' }}>
                      <IconButton onClick={() => handleUnassignClicked(user)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataBox>
      <DataBox
        title={t('jobs.team.tech_operations')}
        handleAdd={canEdit ? () => handleAddOtherUser() : undefined}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {teamOthers?.map((user) => (
                <TableRow key={user.id} style={{ height: '55px' }}>
                  <TableCell component="th" scope="row" style={{ fontWeight: 'bold' }}>
                    {user.fullName}
                  </TableCell>
                  {canEdit && (
                    <TableCell component="th" scope="row" style={{ textAlign: 'right' }}>
                      <IconButton onClick={() => handleUnassignClicked(user)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataBox>
      <UserFormDialog
        open={openUserForm}
        handleClose={() => setOpenUserForm(false)}
        users={users ?? []}
        onAddUser={handleUserAdd}
        fromTA={fromTA}
      />
      <ConfirmActionDialog
        open={showConfirmUnassignDialog}
        handleClose={() => setShowConfirmUnassignDialog(false)}
        handleConfirm={handleUnassignConfirm}
        description={t('jobs.team.unassign', { elem: userToUnassign?.fullName })}
      />
      <Loader isLoading={isUnassigning} />
    </div>
  );
};